import React, { FunctionComponent, useCallback, useMemo } from 'react';

import dateToString from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { Time } from '@yandex-360/iconostasis/dist/icons/Time';
import { Warning } from '@yandex-360/iconostasis/dist/icons/Warning';
import { Tag2, Tag2Props } from '@yandex-360/orb-components/dist/components/Tag2';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { ArtifactStatus } from '@yandex-360/telemost-sdk/models';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { selectFullHistoryArtifactsEnabled } from '../../../../../../shared/features/flags/selectors';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { formatFromTo } from '../../../../helpers/format-date';
import { getConferenceTitle } from '../../../../helpers/get-conference-title';
import { useIsMobileOneColLayout } from '../../../../hooks';
import { selectSelectedSessionId } from '../../../../selectors';
import { ConferenceHistoryItem } from '../../../../types';
import { setSelectedSessionId } from '../../../sidebar/slice';
import styles from './styles.css';

interface ConferenceProps {
    conference: ConferenceHistoryItem;
}

export const Conference: FunctionComponent<ConferenceProps> = ({ conference }) => {
    const isOneColLayout = useIsMobileOneColLayout();
    const {
        hasLocalRecording,
        cloudRecordingStatus,
        summarizationStatus,
        hasMeetingReport,
        sessionId,
        shortUrlId,
        startedAt,
        endedAt,
    } = conference;
    const getTagIconProps = useCallback((status?: ArtifactStatus): Pick<Tag2Props, 'startSlotType' | 'startSlot'> => {
        if (status === ArtifactStatus.IN_PROGRESS) {
            return { startSlotType: 'icon', startSlot: <Time variant="outline" size="sm" /> };
        }
        if (status === ArtifactStatus.FAILED) {
            return { startSlotType: 'icon', startSlot: <Warning variant="outline" size="sm" /> };
        }
        return { startSlotType: 'none' };
    }, []);
    const historyFullArtifacts = useSelector(selectFullHistoryArtifactsEnabled);
    const atLeastOneTagVisible = useMemo(() => {
        return (
            (historyFullArtifacts && (hasLocalRecording || !!cloudRecordingStatus || !!summarizationStatus)) ||
            hasMeetingReport
        );
    }, [cloudRecordingStatus, hasLocalRecording, hasMeetingReport, historyFullArtifacts, summarizationStatus]);
    const actions = useActions({ setSelectedSessionId });
    const selectedSessionId = useSelector(selectSelectedSessionId);
    const onConfenreceClick = useCallback(() => {
        if (selectedSessionId !== sessionId) {
            countMetrika(
                'meeting_history',
                'meeting_click',
                shortUrlId,
                dateToString(new Date(startedAt), 'YYYY-MM-DD')
            );
            actions.setSelectedSessionId(sessionId);
        }
    }, [actions, selectedSessionId, sessionId, shortUrlId, startedAt]);
    return (
        <li
            className={cn(styles.conference, {
                [styles.oneColLayout]: isOneColLayout,
                [styles.selected]: selectedSessionId === sessionId,
            })}
            onClick={onConfenreceClick}
        >
            <Text
                className={styles.beginEnd}
                typography={isOneColLayout ? 'caption-md-regular' : 'body-lg-regular'}
                color={isOneColLayout ? 'secondary' : 'primary'}
            >
                {formatFromTo(startedAt, endedAt)}
            </Text>
            <div className={styles.nameAndTags}>
                <Text typography="body-lg-regular" className={styles.name} maxLines={2} overflow="ellipsis">
                    {getConferenceTitle(conference)}
                </Text>
                {atLeastOneTagVisible && (
                    <div className={styles.tags}>
                        {historyFullArtifacts && (hasLocalRecording || !!cloudRecordingStatus) && (
                            <Tag2 mode="status" {...getTagIconProps(cloudRecordingStatus)} size="xs">
                                {i18n('conferences-history', 'recording')}
                            </Tag2>
                        )}
                        {historyFullArtifacts && !!summarizationStatus && (
                            <Tag2 mode="status" {...getTagIconProps(summarizationStatus)} size="xs">
                                {i18n('conferences-history', 'summarization')}
                            </Tag2>
                        )}
                        {!!hasMeetingReport && (
                            <Tag2 mode="status" size="xs">
                                {i18n('conferences-history', 'meeting-report')}
                            </Tag2>
                        )}
                    </div>
                )}
            </div>
        </li>
    );
};
